/*
Purpose: Centre "accessible services" entitlement context for gating optional features.
Project: TCMS

A centre's enabled services live in tbl_center_accessible_services (backend mode
`getCurrentCentreServices`, which returns the services active *today*). This module
fetches that list once when the centre layout mounts and exposes it via context so
both the sidebar (menu visibility) and route guards can react to it.

The list is cached in component state for the life of the page session, so navigating
between routes does not refetch. A full page reload / direct-URL hit remounts the
provider, which triggers a fresh backend query — so allow/block decisions are always
based on a current response. The result is also mirrored into sessionStorage
(`accessible_services`) to match the app's existing session-state conventions.
*/

import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { BASE_URL, security_key } from './global';

// Service identifier for the new booking/slot tool. Kept in one place so it can be
// renamed in a single edit if the agreed service type changes.
export const BOOKING_TOOL_SERVICE = 'booking_tool';

// sessionStorage key holding the cached list of active service_type strings.
const CACHE_KEY = 'accessible_services';

// Read the cached service list synchronously, or null if it has never been fetched
// this session (so callers can distinguish "no services" from "not loaded yet").
function readCachedServices() {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (raw === null) return null;
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
        return [];
    }
}

// Query the centre's currently-active services and cache them. Best-effort: on any
// failure the centre is treated as having no optional services (fail closed) and the
// cache is written so callers stop showing a loading state. Shared by the provider
// (on layout mount) and the login flow (to preload before landing in the app).
export async function fetchAccessibleServices() {
    const fk_cent_key = sessionStorage.getItem('key');
    try {
        const res = await axios.post(BASE_URL + 'API/api_index.php', {
            security_key: security_key,
            mode: 'getCurrentCentreServices',
            fk_cent_key: fk_cent_key,
        });
        const rows = (res.data && res.data.success === true && Array.isArray(res.data.data))
            ? res.data.data
            : [];
        const types = rows.map(r => r.service_type);
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(types));
        return types;
    } catch (e) {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify([]));
        return [];
    }
}

// `services`: null while the first lookup is in flight (and nothing cached), then an
// array of active service_type strings. Consumers use `services === null` to detect
// "still loading".
const ServiceAccessContext = createContext({ services: null, hasService: () => false });

export function useServiceAccess() {
    return useContext(ServiceAccessContext);
}

export function ServiceAccessProvider({ children }) {
    // Seed from the cached list (populated at login / a previous load) so gated menu
    // items and routes render correctly on first paint. Then revalidate in the
    // background so a full reload / direct-URL hit reflects a current backend response.
    const [services, setServices] = useState(readCachedServices);

    useEffect(() => {
        let alive = true;
        fetchAccessibleServices().then(types => {
            if (alive) setServices(types);
        });
        return () => { alive = false; };
    }, []);

    const value = {
        services,
        hasService: (name) => Array.isArray(services) && services.includes(name),
    };

    return (
        <ServiceAccessContext.Provider value={value}>
            {children}
        </ServiceAccessContext.Provider>
    );
}
